import type { Math } from "@pdfking/quantity"
import { Ast } from "@pdfking/quantity"

import { FolderSpecList as S } from "#internal"
import type { GrowEdge, GrowLayout } from "#public"
import {
  Along,
  Content,
  DIM_DEPTH,
  DIM_HEIGHT_FACTOR,
  DIM_MM,
  DIM_TIMES,
  DIM_WIDTH_FACTOR,
  DIM_SHEET_DEPTH,
  DIM_ZERO,
  Flap,
  FlapPiece,
  Fold,
  NO_PAGES,
  Outward,
  Pages,
  RecordByPrimaryKey,
  makeGrowEntry,
  GrowContentKind,
} from "#public"

const w = DIM_WIDTH_FACTOR
const h = DIM_HEIGHT_FACTOR

type Taper = [Math, Math]
const SQUARE: Taper = [DIM_ZERO, DIM_ZERO]

// A folder's spine creases never lie quite flat in the 3D, nor the first
// flap off each cover: a second one on the same cover would cut through it.
const AJAR = 0.35

// The back B, the front A, the inner panel C, named as a cover's: each a
// poster of its own, no book's cover. A gusset is measured as the spine is.
const back = (extra: Math | undefined): GrowLayout =>
  Content(GrowContentKind.PAGE, extra ? Ast.Add(w(1), extra) : w(1), Pages(4, 3, { label: "B" }))
const front = (): GrowLayout => Content(GrowContentKind.PAGE, w(1), Pages(1, 2, { label: "A" }))
const spine = (depth: Math = DIM_DEPTH): GrowLayout => Content(GrowContentKind.SPINE, depth)

// A flap `depth` deep off `edge` of the layout before it, from `from` past
// the edge's start for `length` — the whole edge when none —, its tip
// `taper` in, `tabs` hanging off it; on a `gusset` that deep, a quarter
// turn at each crease so it stands as deep, flat when none. It folds in once
// the folder is open, at `step`; glued, at 0, it never opens.
const pocket = (
  edge: GrowEdge,
  depth: Math,
  gusset: Math | undefined,
  from: Math = DIM_ZERO,
  length: Math | undefined = undefined,
  taper: Taper = SQUARE,
  tabs: GrowLayout[] = [],
  step: number = 2
): GrowLayout =>
  Flap(
    edge,
    Outward(
      edge,
      gusset
        ? [spine(gusset), Fold(90, step), FlapPiece(depth), ...tabs]
        : [FlapPiece(depth), ...tabs]
    ),
    { angle: gusset ? 90 : 180, step, from, length, taper }
  )

// A glue tab `depth` deep off `edge` of a pocket, its ends cut in `taper`,
// past a `gusset` that deep, when any: glued under the pocket, it never
// opens, nor the pocket it holds (step 0).
const glue = (
  edge: GrowEdge,
  depth: Math,
  gusset: Math | undefined,
  taper: Taper = [DIM_MM(5), DIM_MM(5)]
): GrowLayout =>
  Flap(
    edge,
    gusset ? Outward(edge, [spine(gusset), Fold(90, 0), FlapPiece(depth)]) : FlapPiece(depth),
    {
      angle: 90,
      step: 0,
      taper,
    }
  )

// A lock tab `depth` deep off `edge`, `from` past its start for `length`:
// one with its flap, never folded off it.
const tab = (edge: GrowEdge, depth: Math, from: Math, length: Math): GrowLayout =>
  Flap(edge, FlapPiece(depth), { from, length })

// The inner panel C, `share` of the cover wide, folded over on a `gusset`.
const panel = (edge: GrowEdge, share: number, gusset: Math = DIM_DEPTH): GrowLayout =>
  Flap(
    edge,
    Outward(edge, [
      spine(gusset),
      Fold(90, 2),
      Content(GrowContentKind.PAGE, w(share), { ...NO_PAGES, label: "C" }),
    ]),
    { angle: 90, step: 2 }
  )

// The folder closes first, back and front a quarter turn each off the spine.
const SPINE_FOLD = Fold(90, 1, AJAR)

// `parts` off a cover, the first flap among them that opens ajar: a glued
// one never does
const ajar_first = (parts: GrowLayout[]): GrowLayout[] => {
  const first = parts.findIndex(part => part.enum === "flap" && part.step)
  return parts.map((part, i) => (i === first ? { ...part, ajar: AJAR } : part))
}

// The die as a cover's: the back, `extra` wider than the front, the spine
// and the front, the row as high as the cover; what hangs off the back and
// off the front — flaps, pockets, panels — is tagged as such, its gussets
// measured as the spine is; `mirrored`, the front first, as its die is
// drawn. The die-lines are drawn by the template.
const folder = <I extends string>(
  id: I,
  name: string,
  off_back: GrowLayout[],
  off_front: GrowLayout[],
  extra: Math | undefined = undefined,
  mirrored: boolean = false
) => {
  const covers = [back(extra), ...ajar_first(off_back)]
  const fronts = [front(), ...ajar_first(off_front)]
  const [first, last] = mirrored ? [fronts, covers] : [covers, fronts]
  return makeGrowEntry(
    id,
    name,
    "FOLDER",
    Along([...first, SPINE_FOLD, spine(), SPINE_FOLD, ...last], h(1)),
    {
      is_single_page: true,
      show_cut: false,
      show_crossmark: false,
      show_security: false,
    }
  )
}

// A quarter round pocket off the back's outer edge, from its bottom, and a
// flap under the back locked by a tab at its middle into a slit in the
// pocket: Frutiger, in each size its template has its own lengths for.
const frutiger = (spec: typeof S.frutiger): GrowLayout[] => {
  const flap_width = Ast.Sub(w(1), DIM_MM(spec.flap[0]))
  return [
    pocket(
      "left",
      w(spec.pocket[0] / spec.cover[0]),
      DIM_DEPTH,
      h(1 - spec.pocket[1] / spec.cover[1]),
      h(spec.pocket[1] / spec.cover[1])
    ),
    pocket("bottom", DIM_MM(spec.flap[1]), DIM_DEPTH, DIM_ZERO, flap_width, SQUARE, [
      tab(
        "bottom",
        DIM_MM(spec.tab[1]),
        DIM_TIMES(Ast.Sub(flap_width, DIM_MM(spec.tab[0])), 0.5),
        DIM_MM(spec.tab[0])
      ),
    ]),
  ]
}

// Three flaps round the back on gussets: the side one `side` of the cover
// deep, its tip `side_taper` in at its top and its bottom; the top and
// bottom ones `ends` of the cover deep, each `inset` in from the back's
// sides, their tips `ends_taper` in at their left and their right.
const three_flaps = (
  side: number,
  side_taper: readonly [number, number],
  ends: number,
  ends_taper: readonly [number, number],
  inset: readonly [number, number],
  extra: Math | undefined = undefined
): GrowLayout[] => {
  const [side_depth, end_depth] = [w(side), h(ends)]
  const across = extra ? Ast.Add(w(1), extra) : w(1)
  const length = Ast.Sub(across, DIM_MM(inset[0] + inset[1]))
  const taper: Taper = [DIM_TIMES(end_depth, ends_taper[0]), DIM_TIMES(end_depth, ends_taper[1])]
  return [
    pocket("left", side_depth, DIM_DEPTH, DIM_ZERO, undefined, [
      DIM_TIMES(side_depth, side_taper[0]),
      DIM_TIMES(side_depth, side_taper[1]),
    ]),
    pocket("top", end_depth, DIM_DEPTH, DIM_MM(inset[0]), length, taper),
    pocket("bottom", end_depth, DIM_DEPTH, DIM_MM(inset[0]), length, taper),
  ]
}

// A rounded pocket under a cover, `share` of it wide, from its `edge` side,
// with a glue tab `tab` deep off its outer side, past a gusset as deep as
// its own, `gusset`: back down to the cover.
const POCKET_DEPTH = h(S.glued_pocket.depth)
const glued_pocket = (
  edge: "left" | "right",
  share: number,
  tab: number,
  gusset: Math = DIM_DEPTH
): GrowLayout =>
  pocket(
    "bottom",
    POCKET_DEPTH,
    gusset,
    edge === "left" ? DIM_ZERO : w(1 - share),
    w(share),
    SQUARE,
    [glue(edge, DIM_MM(tab), gusset)],
    0
  )

// A gusset `sheets` sheets short of `gusset`, none round a spine too thin
const short = (gusset: Math, sheets: number): Math =>
  Ast.Max(DIM_ZERO, Ast.Sub(gusset, DIM_TIMES(DIM_SHEET_DEPTH, sheets)))
// The top of a cover's stack when both covers hold one: half the spine
const HALF = short(DIM_TIMES(DIM_DEPTH, 0.5), S.shared_spine)
// What lies under a panel on a `gusset`
const under = (gusset: Math): Math => short(gusset, S.under_panel)

/** A grow per folder template: the page is the closed folder, the grow its
 * open die round the imposition's depth; the die-lines are drawn by the
 * template. Each side grows linearly, as the template's die does (held to it
 * by imposition's folders test): plain data, for Python too. */
export const FolderGrowList = RecordByPrimaryKey([
  // A panel folded over the back, a flap under it
  folder(
    "alieron",
    "Alieron",
    [
      panel("left", S.alieron.panel),
      pocket("bottom", h(S.alieron.bottom), under(DIM_DEPTH), DIM_ZERO, undefined, [
        DIM_TIMES(h(S.alieron.bottom), S.alieron.bottom_taper[0]),
        DIM_TIMES(h(S.alieron.bottom), S.alieron.bottom_taper[1]),
      ]),
    ],
    []
  ),
  folder("arial_narrow", "Arial Narrow", [], []),
  folder("arial_rounded", "Arial Rounded", [], []),
  // A side flap and a bottom flap on the back, tapered
  folder(
    "avant_garde",
    "Avant Garde",
    [
      pocket("left", w(S.avant_garde.side), DIM_DEPTH, DIM_ZERO, undefined, [
        DIM_TIMES(w(S.avant_garde.side), S.avant_garde.side_taper[0]),
        DIM_TIMES(w(S.avant_garde.side), S.avant_garde.side_taper[1]),
      ]),
      pocket(
        "bottom",
        h(S.avant_garde.bottom),
        DIM_DEPTH,
        DIM_ZERO,
        Ast.Sub(w(1), DIM_MM(S.avant_garde.bottom_short)),
        [
          DIM_TIMES(h(S.avant_garde.bottom), S.avant_garde.bottom_taper[0]),
          DIM_TIMES(h(S.avant_garde.bottom), S.avant_garde.bottom_taper[1]),
        ]
      ),
    ],
    []
  ),
  folder("bauhaus", "Bauhaus", [], []),
  // A pocket off the back's outer edge, a half round flap under it
  folder(
    "bookman",
    "Bookman",
    [
      pocket(
        "left",
        w(S.bookman.pocket[0]),
        DIM_DEPTH,
        h(S.bookman.pocket_top),
        h(S.bookman.pocket[1])
      ),
      pocket(
        "bottom",
        Ast.Sub(w(0.5), DIM_MM(S.bookman.flap_inset)),
        DIM_DEPTH,
        DIM_MM(S.bookman.flap_inset),
        Ast.Sub(w(1), DIM_MM(2 * S.bookman.flap_inset))
      ),
    ],
    []
  ),
  folder(
    "brandon",
    "Brandon",
    three_flaps(
      S.brandon.side,
      S.brandon.side_taper,
      S.brandon.ends,
      S.brandon.ends_taper,
      S.brandon.inset
    ),
    []
  ),
  // A pocket on a gusset, its glue tab under it
  folder(
    "calibri",
    "Calibri",
    [
      pocket(
        "left",
        w(S.calibri.pocket[0]),
        DIM_DEPTH,
        h(S.calibri.pocket_top),
        h(S.calibri.pocket[1]),
        SQUARE,
        [
          glue("bottom", DIM_MM(S.calibri.glue), DIM_DEPTH, [
            DIM_MM(S.calibri.glue_taper[0]),
            DIM_MM(S.calibri.glue_taper[1]),
          ]),
        ],
        0
      ),
    ],
    []
  ),
  // Three panels, a rounded pocket glued under the back by its tab under
  // the panel
  folder(
    "castellar",
    "Castellar",
    [
      panel("left", S.castellar.panel),
      glued_pocket("left", S.castellar.pocket, S.castellar.glue, under(DIM_DEPTH)),
    ],
    []
  ),
  // Three flaps round the back
  folder(
    "century_gothic",
    "Century Gothic",
    three_flaps(
      S.century_gothic.side,
      S.century_gothic.side_taper,
      S.century_gothic.ends,
      S.century_gothic.ends_taper,
      [0, 0]
    ),
    []
  ),
  // An index tab out of the back's top, cut away at 45° under it
  folder(
    "didot",
    "Didot",
    [
      Flap("left", FlapPiece(DIM_MM(S.didot.tab)), {
        length: Ast.Add(h(S.didot.tab_at), DIM_MM(S.didot.tab)),
        taper: [DIM_ZERO, DIM_MM(S.didot.tab)],
      }),
    ],
    []
  ),
  folder("frutiger", "Frutiger", frutiger(S.frutiger), []),
  // A pocket off the back's outer edge, wider by its gussets but never past
  // the back it folds onto, a glue tab under it
  folder(
    "futura_light",
    "Futura Light",
    [
      pocket(
        "left",
        Ast.Min(Ast.Add(w(S.futura_light.pocket[0]), DIM_DEPTH, DIM_DEPTH), w(1)),
        DIM_DEPTH,
        h(S.futura_light.pocket_top),
        h(S.futura_light.pocket[1]),
        SQUARE,
        [glue("bottom", DIM_MM(S.futura_light.glue), DIM_DEPTH)],
        0
      ),
    ],
    []
  ),
  // A side flap from below the spine's top to the back's bottom, cut back
  // at its top; a bottom flap hooked into it, its outer side at 45°
  folder(
    "hetfy",
    "Hetfy",
    [
      pocket("left", w(S.hetfy.side), DIM_DEPTH, h(S.hetfy.side_top), h(S.hetfy.side_length), [
        Ast.Min(h(S.hetfy.side_cut), Ast.Sub(h(S.hetfy.side_length), DIM_MM(S.hetfy.side_least))),
        DIM_ZERO,
      ]),
      pocket(
        "bottom",
        h(S.hetfy.bottom),
        DIM_DEPTH,
        DIM_ZERO,
        Ast.Sub(w(1), DIM_MM(S.hetfy.bottom_short)),
        [h(S.hetfy.bottom), DIM_TIMES(h(S.hetfy.bottom), S.hetfy.bottom_taper)]
      ),
    ],
    []
  ),
  // Rounded flaps, the top and bottom ones cut at 45° by the spine; the
  // back is wider than the front
  folder(
    "impact",
    "Impact",
    three_flaps(
      S.impact.side,
      [0, 0],
      S.impact.ends,
      S.impact.ends_taper,
      S.impact.inset,
      DIM_MM(S.impact.extra)
    ),
    [],
    DIM_MM(S.impact.extra)
  ),
  // Castellar's, mirrored as its die is drawn, a pocket glued under the
  // front too by a tab out of the die's side
  folder(
    "rotis",
    "Rotis",
    [
      panel("right", S.rotis.panel, HALF),
      glued_pocket("right", S.rotis.back.pocket, S.rotis.back.glue, under(HALF)),
    ],
    [glued_pocket("left", S.rotis.front.pocket, S.rotis.front.glue, HALF)],
    undefined,
    true
  ),
  folder(
    "trinite",
    "Trinite",
    [
      pocket("left", w(S.trinite.side), DIM_DEPTH, DIM_ZERO, undefined, [
        DIM_TIMES(w(S.trinite.side), S.trinite.side_taper),
        DIM_TIMES(w(S.trinite.side), S.trinite.side_taper),
      ]),
      pocket(
        "bottom",
        h(S.trinite.bottom),
        DIM_DEPTH,
        DIM_ZERO,
        Ast.Sub(w(1), DIM_MM(S.trinite.bottom_short)),
        [
          DIM_TIMES(h(S.trinite.bottom), S.trinite.bottom_taper),
          DIM_TIMES(h(S.trinite.bottom), S.trinite.bottom_taper),
        ]
      ),
    ],
    []
  ),
  // Two panels, a pocket under each, glue tabs out of the sides
  folder(
    "univers",
    "Univers",
    [glued_pocket("left", S.univers.back.pocket, S.univers.back.glue, HALF)],
    [glued_pocket("right", S.univers.front.pocket, S.univers.front.glue, HALF)]
  ),
  folder("verlag", "Verlag", [], []),
])
